import { z } from 'zod';
import { AwardTypeCode, OperationalAwardTypeCode, StageId } from './awards.js';
import { Cents, PlainDate } from './common.js';

const Ref = z.object({ id: z.string(), name: z.string() });

/** What the person typed into Search & jump (APP-022). Shorter than two characters finds nothing. */
export const SearchQuery = z.object({ q: z.string().max(100) });
export type SearchQuery = z.infer<typeof SearchQuery>;

/**
 * A person the searcher may already see (the people scope, never the whole directory). No EE#,
 * email or home org. Directory staff open the person's Admin record; everyone else recognizes them.
 */
export const SearchPerson = z.object({
  id: z.uuid(),
  name: z.string(),
  jobTitle: z.string().nullable(),
  sector: Ref.nullable(),
  /** Left the company: shown to directory staff only, so they can still open the record. */
  left: z.boolean(),
  opens: z.enum(['admin_record', 'recognize']),
});
export type SearchPerson = z.infer<typeof SearchPerson>;

/** An award record the searcher may open (G1): staff, and requesters for their own entries. */
export const SearchAwardRecord = z.object({
  opens: z.literal('record'),
  /** The award's id, for `/awards/:id`. */
  id: z.uuid(),
  /** Drafts have none until they're first sent. */
  displayId: z.string().nullable(),
  type: AwardTypeCode,
  title: z.string().nullable(),
  stage: StageId.nullable(),
  ended: z.enum(['rejected', 'cancelled']).nullable(),
  people: z.number().int().nonnegative(),
  cashCents: Cents,
});

/**
 * The searcher's own award, as My rewards shows it (only once it's theirs to see). A Spot award
 * is one too, once it's released to them (APP-085); before that, search never finds it.
 */
export const SearchOwnReward = z.object({
  opens: z.literal('my_reward'),
  /** The award line's id, for `/my-rewards/:lineId`. */
  id: z.uuid(),
  displayId: z.string().nullable(),
  type: OperationalAwardTypeCode,
  title: z.string().nullable(),
  stage: StageId.nullable(),
  /** While the employee's choice is open: the last day to choose. */
  chooseBy: PlainDate.nullable(),
});

export const SearchAward = z.discriminatedUnion('opens', [SearchAwardRecord, SearchOwnReward]);
export type SearchAward = z.infer<typeof SearchAward>;

/** People and awards; pages come from the web app's own routes, filtered by the same roles. */
export const SearchResponse = z.object({
  people: z.array(SearchPerson),
  awards: z.array(SearchAward),
});
export type SearchResponse = z.infer<typeof SearchResponse>;

/**
 * One thing a person opened, as Recent keeps it (SHELL1, APP-085): its kind and id, nothing
 * else. `award` is a record (`/awards/:id`), `reward` a line in My rewards, `person` a person.
 */
export const RecentRef = z
  .string()
  .regex(/^(person|award|reward):[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/);
export type RecentRef = z.infer<typeof RecentRef>;

/** How many recent opens are kept, and re-checked at once. */
export const RECENT_LIMIT = 8;

/**
 * `GET /search/recent?refs=`: the recent opens to re-check, comma-separated. The answer has only
 * those the person may still open, through the same scopes as a search, with today's labels.
 */
export const RecentQuery = z.object({
  refs: z
    .string()
    .max(RECENT_LIMIT * 50)
    .transform((value) => (value === '' ? [] : value.split(',')))
    .pipe(z.array(RecentRef).max(RECENT_LIMIT)),
});
export type RecentQuery = z.infer<typeof RecentQuery>;
